import { useEffect } from 'react';
import { websocketService, TaskEventType, TaskEvent } from '../services/websocket';
import { useTaskStore } from '../stores/taskStore';
import { useAuthStore } from '../stores/authStore';

/**
 * 任务实时同步Hook
 * 监听WebSocket任务事件并更新本地状态
 */
export const useTaskSync = () => {
  const syncTaskCreated = useTaskStore((state) => state.syncTaskCreated);
  const syncTaskUpdated = useTaskStore((state) => state.syncTaskUpdated);
  const syncTaskDeleted = useTaskStore((state) => state.syncTaskDeleted);
  const syncTasksReordered = useTaskStore((state) => state.syncTasksReordered);
  const user = useAuthStore((state) => state.user);

  useEffect(() => {
    // 定义handler函数
    const handleTaskCreated = (data: TaskEvent) => {
      if (data.task) {
        syncTaskCreated(data.task);
      }
    };

    // 监听任务更新事件
    const handleTaskUpdated = (data: TaskEvent) => {
      if (data.task) {
        syncTaskUpdated(data.task);
      }
    };

    // 监听任务删除事件
    const handleTaskDeleted = (data: TaskEvent) => {
      if (data.taskId) {
        syncTaskDeleted(data.taskId);
      }
    };

    // 监听任务重排序事件
    const handleTasksReordered = (data: TaskEvent) => {
      if (data.taskIds && data.taskIds.length > 0) {
        syncTasksReordered(data.taskIds);
      }
    };

    // 注册任务事件监听器的函数
    const registerTaskListeners = () => {
      websocketService.onTaskEvent(TaskEventType.TASK_CREATED, handleTaskCreated);
      websocketService.onTaskEvent(TaskEventType.TASK_UPDATED, handleTaskUpdated);
      websocketService.onTaskEvent(TaskEventType.TASK_DELETED, handleTaskDeleted);
      websocketService.onTaskEvent(TaskEventType.TASK_REORDERED, handleTasksReordered);
    };

    // 移除任务事件监听器的函数
    const unregisterTaskListeners = () => {
      websocketService.offTaskEvent(TaskEventType.TASK_CREATED, handleTaskCreated);
      websocketService.offTaskEvent(TaskEventType.TASK_UPDATED, handleTaskUpdated);
      websocketService.offTaskEvent(TaskEventType.TASK_DELETED, handleTaskDeleted);
      websocketService.offTaskEvent(TaskEventType.TASK_REORDERED, handleTasksReordered);
    };

    // 获取socket
    let socket = websocketService.getSocket();
    let checkInterval: NodeJS.Timeout | null = null;

    // 如果socket尚未创建，定期检查直到创建
    if (!socket) {
      checkInterval = setInterval(() => {
        socket = websocketService.getSocket();
        if (socket) {
          clearInterval(checkInterval!);

          // 如果已经连接，立即注册监听器
          if (socket.connected) {
            registerTaskListeners();
          }

          // 监听WebSocket连接成功事件
          socket.on('connect', () => {
            registerTaskListeners();
          });

          // 监听WebSocket断开事件
          socket.on('disconnect', () => {
            unregisterTaskListeners();
          });
        }
      }, 100);

      // 清理函数
      return () => {
        if (checkInterval) {
          clearInterval(checkInterval);
        }
      };
    }

    // Socket已存在
    // 如果已经连接，立即注册监听器
    if (socket.connected) {
      registerTaskListeners();
    }

    // 监听WebSocket连接成功事件
    const handleConnect = () => {
      registerTaskListeners();
    };

    // 监听WebSocket断开事件
    const handleDisconnect = () => {
      unregisterTaskListeners();
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // 清理函数
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      unregisterTaskListeners();
    };
  }, [syncTaskCreated, syncTaskUpdated, syncTaskDeleted, syncTasksReordered, user?.id]);
};
